import React, { useContext, useState } from 'react';
import { IconButton, TextField } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { css } from '@emotion/react';
import { UserContext } from '@/components/App';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { greys } from '@/styles/colors';
import {
    AccentBar,
    EmptyState,
    Section,
    SectionHeader,
    SectionNote,
    SectionTitle,
} from '@/components/CorporateShipper/blocks';
import { useCarrierRows } from '@/components/CorporateUsers/Carriers/hooks';
import { isFlexCenterRow, tariffFitsPartner, tariffOwnerLabel } from '@/components/CorporateUsers/Carriers/utils';
import ShipperSettingModals from '@/components/CorporateUsers/Carriers/ExpandedCarrier/ShipperSettingModals';
import { COMPLIANCE_STATUS_BADGES } from '@/components/CorporateUsers/Onboarding/constants';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import { CarrierPill, HeaderBadge, ListCard, ListRow, Panel, RowDetail, RowName } from '../TariffLinks/blocks';
import { useContractorLinks } from './hooks';
import { linkCandidatesFor } from './utils';

const ContractedCarriers = () => {
    const { client, admin_clients } = useContext(UserContext);
    const { state } = useContext(CorpAdminContext);
    const { rows, contractorsByFlexCenter, loading, error } = useCarrierRows();
    const [pricingContractorId, setPricingContractorId] = useState(null);
    const {
        selectedRows,
        setSelectedRows,
        selectedTariff,
        setSelectedTariff,
        linking,
        unlinking,
        linkSelected,
        unlink,
    } = useContractorLinks();

    if (error) return <EmptyState>Couldn&apos;t load contracted carriers.</EmptyState>;
    if (loading) return <EmptyState>Loading contracted carriers…</EmptyState>;

    const flexCenterRow = rows.find((row) => row.client_id === client.client_id && isFlexCenterRow(row));
    if (!flexCenterRow)
        return <EmptyState>This account isn&apos;t one of your organization&apos;s Flex Centers.</EmptyState>;

    const links = contractorsByFlexCenter[flexCenterRow.client_id];
    const linkedCarrierIds = new Set(links.map(({ contractorRow }) => contractorRow.client_id));
    const candidates = linkCandidatesFor(rows, linkedCarrierIds);
    const airportLabel = flexCenterRow.airports.join(' / ');
    const adminClients = admin_clients || [];
    const tariffOptions = state.tariffs.filter((tariff) =>
        tariffFitsPartner(tariff, flexCenterRow.client_id, { forContractor: true, clients: adminClients })
    );
    const tariffLabel = (tariff) => tariff.name || 'Tariff';
    const filterTariffsByNameOrOwner = createFilterOptions({
        stringify: (tariff) => `${tariffLabel(tariff)} ${tariffOwnerLabel(tariff, adminClients)}`,
    });

    let linkedRows = links.map(({ contractorRow, flexCenter }) => {
        const { pricingCard } = flexCenter;
        const complianceBadge = COMPLIANCE_STATUS_BADGES[flexCenter.status];

        return (
            <ListRow key={contractorRow.client_id}>
                <RowName>{contractorRow.business_name}</RowName>
                <RowDetail>
                    {contractorRow.type} · {pricingCard.detail} · {complianceBadge?.label}
                </RowDetail>
                <SecondaryButton onClick={() => setPricingContractorId(contractorRow.client_id)}>
                    {pricingCard.actionLabel}
                </SecondaryButton>
                <CarrierPill>linked · mirrored</CarrierPill>
                <IconButton
                    size="small"
                    aria-label={`Unlink ${contractorRow.business_name}`}
                    disabled={unlinking}
                    onClick={() => unlink(contractorRow, flexCenter.partnership_id)}
                >
                    <Close fontSize="small" />
                </IconButton>
            </ListRow>
        );
    });
    if (links.length === 0) {
        linkedRows = (
            <ListRow>
                <RowDetail>No carriers linked yet.</RowDetail>
            </ListRow>
        );
    }

    let pickerPlaceholder = 'Pick carriers';
    if (candidates.length === 0) pickerPlaceholder = '— no carriers to link yet';
    const linkTarget = airportLabel || flexCenterRow.business_name;
    let flexCenterDetail = 'contracted carriers run routes out of it';
    if (airportLabel) flexCenterDetail = `${airportLabel} · ${flexCenterDetail}`;

    // Looked up on every render so the modal reflects the store after its own mutation lands.
    const pricingLink = links.find(({ contractorRow }) => contractorRow.client_id === pricingContractorId);
    const pricingModal = pricingLink ? (
        <ShipperSettingModals
            row={pricingLink.contractorRow}
            cardKey="pricing"
            shipper={pricingLink.flexCenter}
            defaultPricing={pricingLink.defaultPricing}
            defaultIntegrations={[]}
            onClose={() => setPricingContractorId(null)}
        />
    ) : null;

    return (
        <Panel>
            <Section>
                <SectionHeader>
                    <AccentBar />
                    <SectionTitle>Contracted carriers</SectionTitle>
                    <SectionNote>
                        · link a carrier, then attach a tariff per link · mirrored to the Flex Center&apos;s local
                        account
                    </SectionNote>
                    <HeaderBadge>local account</HeaderBadge>
                </SectionHeader>
                <ListCard>
                    <ListRow>
                        <RowName>{flexCenterRow.business_name}</RowName>
                        <RowDetail>{flexCenterDetail}</RowDetail>
                    </ListRow>
                    {linkedRows}
                    <ListRow>
                        <Autocomplete
                            multiple
                            style={{ flex: 1 }}
                            size="small"
                            disabled={linking}
                            options={candidates}
                            value={selectedRows}
                            onChange={(_event, rowsToLink) => setSelectedRows(rowsToLink)}
                            getOptionLabel={(row) => row.business_name}
                            getOptionSelected={(option, value) => option.client_id === value.client_id}
                            renderOption={(row) => `${row.business_name} · ${row.type}`}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" placeholder={pickerPlaceholder} />
                            )}
                        />
                        <Autocomplete
                            style={{ flex: 1 }}
                            size="small"
                            disabled={linking}
                            options={tariffOptions}
                            value={selectedTariff}
                            onChange={(_event, tariff) => setSelectedTariff(tariff)}
                            getOptionLabel={tariffLabel}
                            getOptionSelected={(option, value) => option.tariff_id === value.tariff_id}
                            filterOptions={filterTariffsByNameOrOwner}
                            renderOption={(tariff) => (
                                <div
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                    `}
                                >
                                    <span>{tariffLabel(tariff)}</span>
                                    <span
                                        css={css`
                                            font-size: 11px;
                                            color: ${greys.primary};
                                        `}
                                    >
                                        {tariffOwnerLabel(tariff, adminClients)}
                                    </span>
                                </div>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" placeholder="no tariff yet — attach later" />
                            )}
                        />
                        <PrimaryButton
                            disabled={selectedRows.length === 0}
                            loading={linking}
                            onClick={() => linkSelected(flexCenterRow)}
                        >
                            + Link to {linkTarget}
                        </PrimaryButton>
                    </ListRow>
                </ListCard>
            </Section>
            {pricingModal}
        </Panel>
    );
};

export default ContractedCarriers;
